import { createFileRoute, Navigate } from "@tanstack/react-router";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/lib/auth-context";
import { useBookmarks, useToggleBookmark } from "@/lib/query-hooks";
import { BookmarkIcon, DownloadIcon, FileTextIcon } from "lucide-react";

export const Route = createFileRoute("/bookmarks")({
  head: () => ({
    meta: [
      { title: "Bookmarks · PN-CivilServices" },
      {
        name: "description",
        content: "The files you've saved for later.",
      },
      { property: "og:title", content: "Bookmarks · PN-CivilServices" },
      { property: "og:description", content: "Your saved study files." },
    ],
  }),
  component: BookmarksPage,
});

function BookmarksPage() {
  const { user, loading } = useAuth();
  const { data, isLoading } = useBookmarks();
  const toggle = useToggleBookmark();
  if (!loading && !user) return <Navigate to="/login" />;

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl px-4 pt-12 pb-24">
        <div className="label-card">Personal ledger</div>
        <h1 className="mt-1 font-display text-3xl font-semibold">Bookmarks</h1>
        <p className="mt-2 text-ink-soft">
          Files you've flagged. Stays in sync across devices when signed in.
        </p>

        <ul className="mt-8 card-ledger divide-y divide-hairline overflow-hidden">
          {isLoading ? (
            <li className="p-6 text-ink-muted text-sm">Loading…</li>
          ) : !data?.length ? (
            <li className="p-10 text-center text-ink-soft">
              No bookmarks yet — tap the flag icon on any file to save it.
            </li>
          ) : (
            data.map((b) => (
              <li key={b.id} className="flex items-center gap-4 px-5 py-3">
                <FileTextIcon className="h-5 w-5 text-ink-soft" />
                <div className="flex-1">
                  <div className="font-medium">
                    {b.file.title ?? b.file.name}
                  </div>
                  <div className="text-xs text-ink-muted">
                    Saved {new Date(b.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <button
                  className="text-paprika hover:text-danger text-xs uppercase tracking-widest font-semibold"
                  onClick={() =>
                    toggle.mutate({ fileId: b.fileId, bookmarked: true })
                  }
                >
                  Remove
                </button>
                {b.file.downloadUrl ? (
                  <a
                    href={b.file.downloadUrl}
                    className="grid h-8 w-8 place-items-center rounded-md text-ink-soft hover:text-paprika"
                  >
                    <DownloadIcon className="h-4 w-4" />
                  </a>
                ) : null}
                <BookmarkIcon className="h-4 w-4 text-paprika" fill="currentColor" />
              </li>
            ))
          )}
        </ul>
      </div>
    </AppShell>
  );
}
